import { hasValue } from '@/Core.Patterns/Factory/validationFactory.ts';

/** `datetime-local` wants local wall-clock time, which toISOString() is not. */
const toDateTimeLocal = (date: Date): string => {
  const pad = (part: number) => String(part).padStart(2, '0');

  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
};

// Chromium `datetime-local` accepts YYYY-MM-DDTHH:mm. A recorded run is often
// "2026-09-19T00:15:00.463" (seconds, fractions, sometimes a trailing Z). Safari
// will still show that string; Chrome and Edge leave the field blank, and the
// editor then looks empty or gets re-seeded with "now".
const DATETIME_LOCAL_PREFIX = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}):(\d{2})/;

/**
 * Strip seconds / fractions / offsets without going through Date, so a stored
 * 00:15 stays 00:15 instead of shifting with the browser timezone.
 *
 * Unparseable non-empty values are returned as-is so a replay is not replaced
 * with the wall clock. Empty / missing values return undefined.
 */
export const toDateTimeLocalValue = (value: unknown): string | undefined => {
  if (!hasValue(value as string | number | Date | null | undefined)) {
    return undefined;
  }

  const raw = String(value);
  const match = raw.match(DATETIME_LOCAL_PREFIX);
  return match ? `${match[1]}T${match[2]}:${match[3]}` : raw;
};

/**
 * Fills in the base-`Input` timestamps an input editor opens without, and
 * normalizes any recorded ones so `datetime-local` can display them.
 *
 * A replayed run arrives with both already recorded, so seeding only bites on
 * Create and Edit, where the fields start empty and an untouched field is left
 * out of the what-if payload entirely. Core then falls back to its own
 * defaults: `CurrentDateTime` becomes the server's clock, and `ReadingDateTime`
 * -- which has no initialiser -- becomes `DateTime.MinValue`, dating the
 * reading to the year 1. Anything reading the clock then judges the run against
 * a time the tester never chose, business hours included.
 */
export const withBaseInputDefaults = (
  inputData: Record<string, unknown> | null | undefined
): Record<string, unknown> => {
  const now = toDateTimeLocal(new Date());
  const seeded: Record<string, unknown> = { ...(inputData ?? {}) };

  seeded.CurrentDateTime = toDateTimeLocalValue(seeded.CurrentDateTime) ?? now;
  seeded.ReadingDateTime = toDateTimeLocalValue(seeded.ReadingDateTime) ?? now;

  return seeded;
};
